import React, { useState, useEffect, useContext, useCallback } from "react";
import "./packs.css";
import PopupDetailsPacks from "./detailsPacks/detailsPacks";
import { PopupContext } from "../../PopupContext";
import axios from "axios";
import Payment from "./paymentSousPacks/payment";
import { IconEye, IconDownload } from "@tabler/icons-react";

/* `icon` = classe d'icône Tabler (déjà utilisée dans ce fichier : "ti ti-x").
   Pour mettre une image à la place, remplace `icon` par `img: "images/packSXMG/xxx.jpg"`. */
const MENU_ITEMS = [
  {
    id: "history",
    img: "images/packSXMG/history.jpg",
    label: "History",
    sub: "View pack history",
  },
  {
    id: "invoices",
    img: "/images/dashboard/mypayement.png",
    label: "Invoices",
    sub: "Pack invoices · download or pay",
  },
  {
    id: "facility",
    img: "images/packSXMG/BalenceRepairService.jpg",
    label: "Balance Facility Service",
    sub: "Total · available · blocked balance",
  },
  {
    id: "repair",
    img: "images/packSXMG/BalenceRepairService.jpg",
    label: "Repair Service",
    sub: "Request a repair",
  },
  {
    id: "buy",
    img: "images/packSXMG/newPackaging.jpg",
    label: "Buy New Packaging",
    sub: "Buy a new pack",
    accent: true,
  },
];

/* ============================================================
   Factures du pack sélectionné (invoiceType = "pack" + packName)
   - Paid    → voir + télécharger
   - Pending → payer = déposer le reçu de paiement (formulaire dans le même popup)
   ============================================================ */
const MAX_RECEIPT_SIZE = 10 * 1024 * 1024; // 10 Mo (limite du serveur)
const ALLOWED_RECEIPT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "N/A";

const PackInvoices = ({ packName, userId, onClose }) => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null); // { type: "ok" | "error", text }
  const [payFor, setPayFor] = useState(null); // facture en cours de paiement (formulaire affiché)
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/invoices/user/${userId}`, {
        params: { type: "pack", packName },
        headers: authHeaders(),
      });
      // filtre de sécurité côté client : uniquement les factures de CE pack
      setInvoices(
        (res.data.invoices || []).filter((inv) => inv.packName === packName),
      );
    } catch (err) {
      setNotice({
        type: "error",
        text: err.response?.data?.message || "Unable to load invoices.",
      });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, userId, packName]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  /* ---------- Voir / télécharger (facture payée uniquement) ---------- */
  const handleView = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/view/${id}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setNotice({ type: "error", text: "Unable to open the invoice." });
    }
  };

  const handleDownload = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/download/${id}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setNotice({
        type: "error",
        text: "Unable to download the invoice.",
      });
    }
  };

  /* ---------- Payer = envoyer le reçu de paiement ---------- */
  const startPay = (inv) => {
    setNotice(null);
    setReceiptFile(null);
    setPayFor(inv);
  };

  const cancelPay = () => {
    setPayFor(null);
    setReceiptFile(null);
  };

  const handleChooseFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!ALLOWED_RECEIPT_TYPES.includes(f.type)) {
      setNotice({
        type: "error",
        text: "Unsupported format: PDF, JPG or PNG only.",
      });
      e.target.value = "";
      return;
    }
    if (f.size > MAX_RECEIPT_SIZE) {
      setNotice({ type: "error", text: "File exceeds 10 MB." });
      e.target.value = "";
      return;
    }
    setNotice(null);
    setReceiptFile(f);
  };

  const handleSendReceipt = async () => {
    if (!receiptFile || !payFor) return;
    const formData = new FormData();
    formData.append("receipt", receiptFile); // nom de champ attendu par le serveur

    setUploading(true);
    try {
      await axios.put(`${API_URL}/invoices/${payFor._id}`, formData, {
        headers: authHeaders(),
      });
      setNotice({
        type: "ok",
        text: "Receipt sent. Your payment is awaiting validation.",
      });
      cancelPay();
      await loadInvoices();
    } catch (err) {
      setNotice({
        type: "error",
        text: err.response?.data?.message || "Failed to send the receipt.",
      });
    } finally {
      setUploading(false);
    }
  };

  /* ---------- Rendu ---------- */
  return (
    <>
      <div className="sp-inv-head">
        <button
          type="button"
          className="sp-inv-back"
          onClick={payFor ? cancelPay : onClose}
          aria-label="Back"
        >
          ‹
        </button>
        <span className="sp-inv-title">Invoices · {packName}</span>
      </div>

      {notice && (
        <div className={`sp-notice sp-notice--${notice.type}`} role="status">
          {notice.text}
        </div>
      )}

      {payFor ? (
        /* Formulaire de dépôt du reçu : remplace la liste dans le même popup */
        <div className="sp-inv-panel">
          <div className="sp-inv-panel-title">Pay invoice</div>
          <div className="sp-sub">
            {packName} · {fmtDate(payFor.invoiceDate)}
          </div>
          <p className="sp-inv-hint">
            Make your payment, then upload the receipt here (PDF, JPG or PNG,
            10 MB max). It will be reviewed by our team.
          </p>
          <input
            className="sp-file"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            disabled={uploading}
            onChange={handleChooseFile}
          />
          {receiptFile && (
            <div className="sp-file-name">{receiptFile.name}</div>
          )}
          <div className="sp-inv-panel-actions">
            <button
              type="button"
              className="sp-btn sp-btn--primary"
              disabled={!receiptFile || uploading}
              onClick={handleSendReceipt}
            >
              {uploading ? "Sending..." : "Send receipt"}
            </button>
            <button
              type="button"
              className="sp-btn"
              disabled={uploading}
              onClick={cancelPay}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className="sp-empty">Loading...</div>
      ) : invoices.length === 0 ? (
        <div className="sp-empty">No invoices for this pack yet.</div>
      ) : (
        <ul className="sp-menu" role="list">
          {invoices.map((inv) => {
            const paid = inv.status === "paid";
            const hasReceipt = !!inv.receiptPath;
            return (
              <li key={inv._id} className="sp-row sp-row--static">
                <span className="sp-icon" aria-hidden="true">
                  <img
                    className="sp-icon-img"
                    src="/images/dashboard/myorder.png"
                    alt=""
                  />
                </span>
                <span className="sp-body">
                  <span className="sp-label">
                    Invoice · {fmtDate(inv.invoiceDate)}
                  </span>
                  <span className="sp-sub">
                    {paid ? (
                      <span className="sp-badge sp-badge--paid">Paid</span>
                    ) : hasReceipt ? (
                      <span className="sp-badge sp-badge--review">
                        Receipt sent · pending
                      </span>
                    ) : (
                      <span className="sp-badge sp-badge--pending">
                        To pay
                      </span>
                    )}
                  </span>
                </span>

                <span className="sp-inv-actions">
                  {paid ? (
                    <>
                      <button
                        type="button"
                        className="sp-inv-btn"
                        title="View"
                        aria-label="View invoice"
                        onClick={() => handleView(inv._id)}
                      >
                        <IconEye size={18} stroke={1.8} />
                      </button>

                      <button
                        type="button"
                        className="sp-inv-btn"
                        title="Download"
                        aria-label="Download invoice"
                        onClick={() => handleDownload(inv._id)}
                      >
                        <IconDownload size={18} stroke={1.8} />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="sp-inv-pay"
                      onClick={() => startPay(inv)}
                    >
                      <i className="ti ti-upload" />{" "}
                      {hasReceipt ? "Replace" : "Pay"}
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

/* ============================================================
   Popup du pack
   ============================================================ */
const PopupPacks = ({ packName, onClose, onShowHistory }) => {
  const { isChildPopupOpen, openChildPopup, closeChildPopup } =
    useContext(PopupContext);

  const [view, setView] = useState(null); // null | 'history' | 'invoices' | 'facility' | 'repair' | 'buy'
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!packName) return;
    axios
      .get(`${API_URL}/packs/${encodeURIComponent(packName)}`)
      .then((r) => {
        setDetails(r.data);
        setLoading(false);
      })
      .catch((e) => {
        setError("Loading error.");
        setLoading(false);
      });
  }, [API_URL, packName]);

  const handleItem = useCallback(
    (id) => {
      if (
        isChildPopupOpen &&
        id !== "history" &&
        id !== "buy" &&
        id !== "invoices"
      )
        return;
      if (id === "facility" || id === "repair") openChildPopup();
      if (id === "history" && onShowHistory) onShowHistory(packName);
      setView(id);
    },
    [isChildPopupOpen, openChildPopup, packName, onShowHistory],
  );

  const handleBack = useCallback(() => {
    if (view === "facility" || view === "repair") closeChildPopup();
    setView(null);
  }, [view, closeChildPopup]);

  if (view === "history") {
    return (
      <div className="sp-content">
        <PopupDetailsPacks
          packName={packName}
          userId={localStorage.getItem("userId")}
          onClose={handleBack}
        />
      </div>
    );
  }

  if (view === "invoices") {
    return (
      <div className="sp-content">
        <PackInvoices
          packName={packName}
          userId={localStorage.getItem("userId")}
          onClose={handleBack}
        />
      </div>
    );
  }

  if (view === "buy") {
    return (
      <div className="sp-content">
        <Payment
          isOpen
          packName={packName}
          userId={localStorage.getItem("userId")}
          onClose={handleBack}
        />
      </div>
    );
  }

  return (
    <div className="sp-content">
      <ul className="sp-menu" role="list">
        {MENU_ITEMS.map((item) => (
          <li
            key={item.id}
            className={`sp-row${item.accent ? " sp-row--accent" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => handleItem(item.id)}
            onKeyDown={(e) => e.key === "Enter" && handleItem(item.id)}
            aria-label={item.label}
          >
            <span className="sp-icon" aria-hidden="true">
              {item.img ? (
                <img src={item.img} alt="" className="sp-icon-img" />
              ) : (
                <i className={item.icon} />
              )}
            </span>
            <span className="sp-body">
              <span className="sp-label">{item.label}</span>
              <span className="sp-sub">{item.sub}</span>
            </span>
            <span className="sp-arrow" aria-hidden="true">
              ›
            </span>
          </li>
        ))}
      </ul>

      {view === "facility" && (
        <div
          className="sp-overlay"
          onClick={handleBack}
          role="dialog"
          aria-modal="true"
          aria-label="Balance Facility Service"
        >
          <div className="sp-sheet" onClick={(e) => e.stopPropagation()}>
            <button
              className="sp-sheet-close"
              onClick={handleBack}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
            <ul className="sp-menu" role="list">
              {[
                {
                  img: "images/packSXMG/totalBalance.jpg",
                  label: "Total Balance",
                  sub: "Total account balance",
                },
                {
                  img: "images/packSXMG/availableAmount.jpg",
                  label: "Available Amount",
                  sub: "Available amount",
                },
                {
                  img: "images/packSXMG/blockedAmount.jpg",
                  label: "Blocked Amount",
                  sub: "Blocked amount",
                },
              ].map((r) => (
                <li key={r.label} className="sp-row" role="listitem">
                  <span className="sp-icon" aria-hidden="true">
                    <img src={r.img} alt="" className="sp-icon-img" />
                  </span>
                  <span className="sp-body">
                    <span className="sp-label">{r.label}</span>
                    <span className="sp-sub">{r.sub}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(PopupPacks);